import { useEffect, useRef, useState } from "react";

/**
 * Hook to detect when an element enters the viewport
 * Used for fade-in animations on scroll
 */
export const useFadeIn = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || "0px 0px -50px 0px",
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

/**
 * Component wrapper for fade-in animations
 */
export const FadeIn = ({ 
  children, 
  delay = 0, 
  className = "",
  as: Component = "div" 
}) => {
  const [ref, isVisible] = useFadeIn();

  const delayClass = delay > 0 ? `delay-${delay}` : "";

  return (
    <Component
      ref={ref}
      className={`fade-section ${isVisible ? "visible" : ""} ${delayClass} ${className}`}
    >
      {children}
    </Component>
  );
};

export default FadeIn;
